// Finance > Billing (AP) — the vendor-bill WORK QUEUE (handoff W26 / W16).
//
// Hits GET /bills?status=&bucket= and splits the same bill records into the
// jobs finance does:
//   To review   — draft / pending review: check against the PO, then approve
//   Disputed    — sent back to the vendor with a reason
//   Approved    — approved to pay, NOT paid yet (approval ≠ payment)
//   Due in 7 days / Overdue — unpaid bills by due date (server buckets)
//   Paid / All
// Each row opens the bill record (/bills/:id) in the same tab. The summary
// strip reads the Finance dashboard rollup (server totals) — no money is
// added up on this page.
//
// URL params `status` / `bucket` still pre-filter on landing so the Finance
// dashboard links keep working (e.g. /finance/billing?bucket=overdue).
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Plus, Upload, X } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";
import {
  FilterTabs,
  MetricStrip,
  TABLE_HEAD,
  TABLE_WRAP,
  TD,
  TH,
  TROW,
} from "@/components/ui/RecordParts";
import { StatusPill } from "@/components/ui/StatusPill";
import { cn } from "@/lib/utils";
import { useAuth } from "@/auth/AuthProvider";
import { EnterBillModal } from "@/components/finance/EnterBillModal";
import { MissingBillsTable, useMissingBills } from "@/components/finance/MissingBillsTable";
import { billsCsvName, billsToCsv } from "@/lib/bills-csv";

type BillStatus =
  | "draft"
  | "pending_review"
  | "approved"
  | "disputed"
  | "paid"
  | "voided";
type BillBucket = "current" | "due_soon" | "overdue" | "paid" | "voided";

interface BillRow {
  id: string;
  vendorId: string;
  vendorName: string;
  purchaseOrderId: string | null;
  poNumber: number | null;
  vendorBillNumber: string | null;
  billDate: string;
  dueDate: string | null;
  status: BillStatus;
  total: string | null;
  bucket: BillBucket;
  paidAt: string | null;
  approvedAt: string | null;
  createdAt: string;
}

interface ListResponse {
  rows: BillRow[];
  nextCursor: { createdAt: string; id: string } | null;
}

interface BucketCell {
  count: number;
  amount: string;
}
interface DashboardResponse {
  apSummary: {
    totalOpenCount: number;
    totalOpenAmount: string;
    pendingReviewCount: number;
    current: BucketCell;
    dueSoon: BucketCell;
    overdue: BucketCell;
  };
  fetchedAt: string;
}

type View = "review" | "missing" | "disputed" | "approved" | "due_soon" | "overdue" | "paid" | "all";

const VIEWS: ReadonlyArray<{ value: View; label: string }> = [
  { value: "review", label: "To review" },
  // POs shipped/delivered with no bill yet (Stephanie 2026-10-20).
  { value: "missing", label: "Missing a bill" },
  { value: "disputed", label: "Disputed" },
  { value: "approved", label: "Approved" },
  { value: "due_soon", label: "Due in 7 days" },
  { value: "overdue", label: "Overdue" },
  { value: "paid", label: "Paid" },
  { value: "all", label: "All" },
];

function statusLabel(s: BillStatus): string {
  switch (s) {
    case "draft":
      return "Draft";
    case "pending_review":
      return "To review";
    case "approved":
      return "Approved · not paid";
    case "disputed":
      return "Disputed";
    case "paid":
      return "Paid";
    case "voided":
      return "Voided";
  }
}

function statusTone(s: BillStatus): "active" | "info" | "dormant" | "closed" | "warn" {
  switch (s) {
    case "draft":
    case "pending_review":
      return "warn";
    case "approved":
      return "info";
    case "disputed":
      return "warn";
    case "paid":
      return "active";
    case "voided":
      return "closed";
  }
}

function formatCurrency(amount: string | null): string {
  if (amount === null) return "—";
  const n = Number(amount);
  if (Number.isNaN(n)) return amount;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function formatDate(s: string | null): string {
  if (s === null) return "—";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return new Date(s + "T00:00:00").toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Landing view from ?status= / ?bucket= (Finance dashboard deep links). */
function initialView(
  status: string | null,
  bucket: string | null,
  viewParam: string | null,
): { view: View; current: boolean } {
  if (bucket === "due_soon") return { view: "due_soon", current: false };
  if (bucket === "overdue") return { view: "overdue", current: false };
  if (bucket === "current") return { view: "all", current: true };
  switch (status) {
    case "draft":
    case "pending_review":
      return { view: "review", current: false };
    case "approved":
      return { view: "approved", current: false };
    case "disputed":
      return { view: "disputed", current: false };
    case "paid":
      return { view: "paid", current: false };
  }
  if (viewParam === "missing") return { view: "missing", current: false };
  if (status || bucket || viewParam === "all") return { view: "all", current: false };
  return { view: "review", current: false };
}

export function BillingPage() {
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const initial = initialView(search.get("status"), search.get("bucket"), search.get("view"));

  const [view, setViewState] = useState<View>(initial.view);
  // "Due later than 7 days" (bucket=current) — reachable from the dashboard's
  // Current row; shown as a removable chip on the All tab.
  const [onlyCurrent, setOnlyCurrent] = useState<boolean>(initial.current);
  // Supplier filter (Stephanie 2026-10-22); "" = every supplier.
  const [vendorId, setVendorId] = useState<string>(search.get("vendor") ?? "");
  const vendorsQ = useQuery({
    queryKey: ["vendors", "all"],
    queryFn: () => api<{ rows: Array<{ id: string; name: string }> }>("/vendors"),
    staleTime: 5 * 60 * 1000,
  });
  const vendorList = useMemo(() => [...(vendorsQ.data?.rows ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [vendorsQ.data]);

  function setView(v: View) {
    setViewState(v);
    setOnlyCurrent(false);
    const next = new URLSearchParams();
    if (v === "review") next.set("status", "pending_review");
    if (v === "approved" || v === "disputed" || v === "paid") next.set("status", v);
    if (v === "due_soon" || v === "overdue") next.set("bucket", v);
    if (v === "all") next.set("view", "all");
    if (v === "missing") next.set("view", "missing");
    setSearch(next, { replace: true });
  }

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (view === "review") {
      params.append("status", "draft");
      params.append("status", "pending_review");
    }
    if (view === "approved" || view === "disputed" || view === "paid") params.append("status", view);
    if (view === "due_soon" || view === "overdue") params.append("bucket", view);
    if (view === "all" && onlyCurrent) params.append("bucket", "current");
    if (vendorId) params.set("vendor_id", vendorId);
    params.set("limit", "100");
    return params.toString();
  }, [view, onlyCurrent, vendorId]);

  // Download every bill matching the tab + supplier (all pages, not just
  // the first 100 on screen).
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const download = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const all: BillRow[] = [];
      let cursor: ListResponse["nextCursor"] = null;
      for (let page = 0; page < 50; page++) {
        const qs = new URLSearchParams(queryString);
        qs.set("limit", "200");
        if (cursor) { qs.set("cursor_created_at", cursor.createdAt); qs.set("cursor_id", cursor.id); }
        const r: ListResponse = await api<ListResponse>(`/bills?${qs.toString()}`);
        all.push(...r.rows);
        cursor = r.nextCursor;
        if (!cursor) break;
      }
      const blob = new Blob([billsToCsv(all, (s) => statusLabel(s as BillStatus))], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const vendorName = vendorList.find((v) => v.id === vendorId)?.name;
      a.download = billsCsvName([vendorName, view === "all" ? null : view.replace(/_/g, "-")], new Date().toISOString().slice(0, 10));
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError("Couldn't download the bills. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canManage =
    user?.roles.some((r) => r === "admin" || r === "finance") ?? false;

  const [showEnter, setShowEnter] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["finance", "bills", queryString],
    queryFn: () => api<ListResponse>(`/bills?${queryString}`),
    enabled: view !== "missing",
  });
  const missingQ = useMissingBills();
  const missingCount = missingQ.data?.rows.length ?? null;
  const views = VIEWS.map((o) => (o.value === "missing" && missingCount ? { ...o, label: `Missing a bill (${missingCount})` } : o));

  const dashQ = useQuery({
    queryKey: ["finance", "dashboard"],
    queryFn: () => api<DashboardResponse>("/finance/dashboard"),
  });
  const ap = dashQ.data?.apSummary;

  const rows = data?.rows ?? [];

  const emptyCopy: Record<View, [string, string]> = {
    review: ["Nothing to review.", "New bills land here — check each against its PO, then approve it."],
    disputed: ["No disputed bills.", "A bill you dispute waits here, with its reason, until it's resolved."],
    approved: ["No approved bills waiting.", "Approved bills sit here until they're marked paid. Approval isn't payment."],
    due_soon: ["Nothing due in the next 7 days.", "Unpaid bills show here a week before their due date."],
    overdue: ["No overdue bills.", "Unpaid bills past their due date show here."],
    paid: ["No paid bills yet.", "Bills move here once they're marked paid."],
    all: ["No bills match.", "Try another tab."],
    missing: ["", ""],
  };

  return (
    <div className="mx-auto w-full">
      <PageHeader
        eyebrow="Finance / Billing"
        title="Billing"
        intro="Vendor bills to review against their POs, approve, and pay. Approving a bill doesn't pay it."
        action={
          canManage && (
            <div className="flex flex-wrap gap-2">
              <Link
                to="/finance/quickbooks?entity=bills"
                className="inline-flex h-10 items-center gap-2 rounded-md border border-rule bg-white px-[14px] text-sm font-medium text-ink hover:bg-canvas"
              >
                <Upload className="h-4 w-4" /> Export to QuickBooks
              </Link>
              <Button
                type="button"
                onClick={() => {
                  setBanner(null);
                  setShowEnter(true);
                }}
              >
                <Plus className="h-4 w-4" /> Enter a bill
              </Button>
            </div>
          )
        }
      />

      {banner && (
        <div role="status" className="mb-6 rounded-md border border-ok/30 bg-ok-bg px-4 py-3 text-sm text-ok">
          {banner}
        </div>
      )}

      <div className="mb-7">
        <MetricStrip
          ariaLabel="Billing summary"
          cells={[
            {
              label: "To review",
              value: dashQ.isError ? "—" : ap ? ap.pendingReviewCount : null,
              note: "Check against the PO",
              onClick: () => setView("review"),
              attention: (ap?.pendingReviewCount ?? 0) > 0,
            },
            {
              label: "Open payables",
              value: dashQ.isError ? "—" : ap ? formatCurrency(ap.totalOpenAmount) : null,
              note: ap ? `${ap.totalOpenCount.toLocaleString()} unpaid ${ap.totalOpenCount === 1 ? "bill" : "bills"}` : undefined,
              onClick: () => setView("all"),
            },
            {
              label: "Due in 7 days",
              value: dashQ.isError ? "—" : ap ? formatCurrency(ap.dueSoon.amount) : null,
              note: ap ? `${ap.dueSoon.count.toLocaleString()} ${ap.dueSoon.count === 1 ? "bill" : "bills"}` : undefined,
              onClick: () => setView("due_soon"),
            },
            {
              label: "Overdue",
              value: dashQ.isError ? "—" : ap ? formatCurrency(ap.overdue.amount) : null,
              note: ap ? `${ap.overdue.count.toLocaleString()} ${ap.overdue.count === 1 ? "bill" : "bills"} past due` : undefined,
              onClick: () => setView("overdue"),
              attention: (ap?.overdue.count ?? 0) > 0,
            },
          ]}
        />
        {dashQ.data && (
          <p className="mt-2 text-xs text-muted">
            Open bills as of {new Date(dashQ.data.fetchedAt).toLocaleString()} · due dates come from each bill.
          </p>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-rule">
        <FilterTabs ariaLabel="Bill work queue" options={views} value={view} onChange={setView} />
        {view !== "missing" && (
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <select
              aria-label="Supplier"
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
              className="h-10 rounded-md border border-rule bg-white px-3 text-sm text-ink"
            >
              <option value="">All suppliers</option>
              {vendorList.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => void download()}
              disabled={downloading}
              className="inline-flex h-10 items-center gap-1.5 rounded-md border border-rule bg-white px-3 text-sm font-medium text-ink hover:bg-canvas disabled:opacity-50"
            >
              <Download className="h-4 w-4" aria-hidden /> {downloading ? "Downloading…" : "Download"}
            </button>
          </div>
        )}
        {view === "all" && onlyCurrent && (
          <button
            type="button"
            onClick={() => setOnlyCurrent(false)}
            className="mb-2 inline-flex min-h-[40px] items-center gap-1.5 rounded-md border border-rule bg-white px-3 text-sm text-ink hover:bg-canvas"
          >
            Due in more than 7 days <X className="h-3.5 w-3.5" aria-label="Clear filter" />
          </button>
        )}
      </div>

      {downloadError && <p role="alert" className="mb-3 text-sm font-semibold text-risk">{downloadError}</p>}

      <div className={TABLE_WRAP} role="region" aria-label="Vendor bills" tabIndex={0}>
        {view === "missing" ? (
          <MissingBillsTable />
        ) : isLoading ? (
          <div className="space-y-3 p-5" aria-label="Loading bills">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-4 w-full animate-pulse rounded bg-canvas" />
            ))}
          </div>
        ) : isError ? (
          <div className="p-5 text-sm text-risk" role="alert">
            We couldn't load bills. Nothing has changed — refresh to try again.
          </div>
        ) : rows.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <p className="font-medium text-ink">{emptyCopy[view][0]}</p>
            <p className="mt-1 text-sm text-muted">{emptyCopy[view][1]}</p>
          </div>
        ) : (
          <table className="w-full text-left">
            <thead className={TABLE_HEAD}>
              <tr>
                <th scope="col" className={TH}>Vendor / bill</th>
                <th scope="col" className={`${TH} hidden md:table-cell`}>PO</th>
                <th scope="col" className={TH}>Status</th>
                <th scope="col" className={`${TH} hidden sm:table-cell`}>Due</th>
                <th scope="col" className={`${TH} text-right`}>Total</th>
              </tr>
            </thead>
            <tbody className="text-[13px]">
              {rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => navigate(`/bills/${r.id}`)}
                  className={cn(TROW, "cursor-pointer")}
                >
                  <td className={TD}>
                    <Link
                      to={`/bills/${r.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="font-medium text-ink hover:underline"
                    >
                      {r.vendorName}
                    </Link>
                    <span className="block text-[11px] text-muted">
                      {r.vendorBillNumber ? `Bill ${r.vendorBillNumber}` : "No bill number"} · billed {formatDate(r.billDate)}
                      <span className="md:hidden">{r.poNumber ? ` · PO #${r.poNumber}` : ""}</span>
                    </span>
                  </td>
                  <td className={`${TD} hidden md:table-cell`}>
                    {r.poNumber && r.purchaseOrderId ? (
                      <Link
                        to={`/pos/${r.purchaseOrderId}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-ink hover:underline"
                      >
                        PO #{r.poNumber}
                      </Link>
                    ) : (
                      <span className="text-muted">Not linked</span>
                    )}
                  </td>
                  <td className={TD}>
                    <StatusPill className="normal-case" tone={statusTone(r.status)}>{statusLabel(r.status)}</StatusPill>
                  </td>
                  <td className={`${TD} hidden sm:table-cell`}>
                    <span className={cn(r.bucket === "overdue" ? "text-warn" : "text-ink")}>
                      {formatDate(r.dueDate)}
                    </span>
                    {r.bucket === "overdue" && <span className="block text-[11px] text-warn">Overdue</span>}
                    {r.bucket === "due_soon" && <span className="block text-[11px] text-muted">Due in 7 days</span>}
                  </td>
                  <td className={`${TD} text-right font-medium tabular-nums text-ink`}>
                    {formatCurrency(r.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="mt-3 text-xs text-muted">
        {view === "missing" ? "POs the supplier has shipped or delivered, with no bill entered yet. Click one to enter its bill." : <>Showing {rows.length} {rows.length === 1 ? "bill" : "bills"}</>}
        {view !== "missing" && data?.nextCursor && " · more available — use a narrower tab to find the rest"}
      </p>

      {showEnter && (
        <EnterBillModal
          onCancel={() => setShowEnter(false)}
          onCreated={(bill) => {
            setShowEnter(false);
            setBanner(
              `Bill entered${bill.vendorName ? ` for ${bill.vendorName}` : ""} — it's waiting for review. Open it to check and approve.`,
            );
            void queryClient.invalidateQueries({
              queryKey: ["finance", "bills"],
            });
            void queryClient.invalidateQueries({
              queryKey: ["finance", "dashboard"],
            });
          }}
        />
      )}
    </div>
  );
}
